/**
 * LeaseCard Component
 * Displays lease information in a card format.
 * Uses design tokens for consistent theming.
 */

import { Link } from 'react-router-dom';
import type { LeaseListItem } from '../types/lease';
import { StatusBadge, DataGrid, DataRow } from './layout/index';
import { Button } from './ui/button';
import { formatLocalDate, formatDateTime } from '../utils/date';

interface LeaseCardProps {
  lease: LeaseListItem;
  onArchive?: (lease: LeaseListItem) => void;
  onUnarchive?: (lease: LeaseListItem) => void;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}


export function LeaseCard({ lease, onArchive, onUnarchive }: LeaseCardProps) {
  // An 'ending' or 'ended' lease stays fully visible and manageable — only 'archived'
  // (read-only, BR-LL-11) loses these affordances.
  const canManage = lease.state !== 'archived';
  const canArchive = lease.state !== 'archived';
  const canUnarchive = lease.state === 'archived';

  return (
    <div className="rounded-lg border border-border bg-card p-4 flex flex-col gap-3">
      {/* Header with state badge + actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StatusBadge status={lease.state} />
          {lease.state === 'ending' && lease.endDate && (
            <span
              className="text-xs text-muted-foreground"
              data-testid={`lease-ending-date-${lease.id}`}
            >
              Ending {formatLocalDate(lease.endDate)}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          {canManage && (
            <Button variant="default" size="sm" asChild>
              <Link to={`/leases/${lease.id}`} data-testid={`manage-lease-${lease.id}`}>
                Manage
              </Link>
            </Button>
          )}
          {canArchive && onArchive && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onArchive(lease)}
              className="text-destructive hover:text-destructive"
              data-testid={`archive-lease-${lease.id}`}
            >
              Archive
            </Button>
          )}
          {canUnarchive && onUnarchive && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onUnarchive(lease)}
              data-testid={`unarchive-lease-${lease.id}`}
            >
              Un-archive
            </Button>
          )}
        </div>
      </div>

      {/* Dates + Financials */}
      <DataGrid columns={2}>
        <DataRow label="Start" value={formatLocalDate(lease.startDate)} />
        <DataRow label="End" value={formatLocalDate(lease.endDate)} />
        <DataRow label="Rent" value={formatCurrency(lease.rentAmount / 100)} />
        <DataRow label="Deposit" value={formatCurrency(lease.securityDeposit / 100)} />
      </DataGrid>
      {lease.actualEndDate && (
        <DataGrid columns={1}>
          <DataRow label="Actual End" value={formatLocalDate(lease.actualEndDate)} />
        </DataGrid>
      )}

      {/* Tenants */}
      {lease.tenants.length > 0 && (
        <div className="flex flex-col gap-1.5 pt-2 border-t border-border">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Tenants</span>
          {lease.tenants.map((tenant) => (
            <div key={tenant.id} className="flex flex-col gap-0.5 p-2 bg-accent/50 rounded">
              <span className="text-sm font-medium text-foreground">
                {tenant.firstName} {tenant.lastName}
              </span>
              <span className="text-xs text-muted-foreground">{tenant.email}</span>
              {tenant.phone && (
                <span className="text-xs text-muted-foreground">{tenant.phone}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Footer with metadata */}
      {lease.archivedAt && (
        <div className="pt-2 border-t border-border">
          <span className="text-xs text-muted-foreground italic">
            {/* archivedAt is a Timestamptz, not a calendar date — formatLocalDate's
                date-only "T12:00:00" parsing produced "Invalid Date" here. */}
            Archived on {formatDateTime(lease.archivedAt)}
          </span>
        </div>
      )}
    </div>
  );
}
